import { useState, useEffect, useCallback } from 'react';
import { getCompanies, updateCompany } from '../../services/api';
import './Companies.css';

const SORT_FIELDS = [
  { value: 'companyName', label: 'Company Name' },
  { value: 'industry', label: 'Industry' },
  { value: 'location', label: 'Location' },
  { value: 'contactCount', label: 'Contacts' },
];

const EMPLOYEE_RANGES = [
  { value: '', label: 'All Ranges' },
  { value: '1-50', label: '1 – 50' },
  { value: '51-100', label: '51 – 100' },
  { value: '101-500', label: '101 – 500' },
  { value: '501-1000', label: '501 – 1,000' },
  { value: '1001-2000', label: '1,001 – 2,000' },
  { value: '2001-above', label: '2,001 & Above' },
];

const EMPTY_FILTERS = {
  companyName: '', industry: '', location: '', employeeRange: '',
  sortField: 'companyName', sortDir: 'asc',
};

const EMPTY_EDIT = {
  companyName: '', companyUrl: '', industry: '',
  companyLinkedinUrl: '', location: '', employeeCount: '',
};

export default function Companies({ onViewContacts }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  const [search, setSearch] = useState('');

  const [applied, setApplied] = useState(EMPTY_FILTERS);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_FILTERS);

  const [editCompany, setEditCompany] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  const [viewCompany, setViewCompany] = useState(null);
  const [viewTab, setViewTab] = useState('details');

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page,
        size: pageSize,
        sort: `${applied.sortField},${applied.sortDir}`,
      };
      if (search.trim()) params.search = search.trim();
      if (applied.companyName.trim()) params.companyName = applied.companyName.trim();
      if (applied.industry.trim()) params.industry = applied.industry.trim();
      if (applied.location.trim()) params.location = applied.location.trim();
      if (applied.employeeRange) params.employeeRange = applied.employeeRange;

      const { data } = await getCompanies(params);
      setCompanies(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (e) {
      setError(e.message || 'Failed to load companies.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, applied, search]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const activeFilterCount = [applied.companyName, applied.industry, applied.location, applied.employeeRange].filter(Boolean).length;

  const openDrawer = () => { setDraft({ ...applied }); setDrawerOpen(true); };
  const closeDrawer = () => setDrawerOpen(false);

  const applyFilters = () => { setApplied({ ...draft }); setPage(0); closeDrawer(); };
  const resetFilters = () => { setDraft(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); setPage(0); closeDrawer(); };

  const handleDraft = (e) => {
    const { name, value } = e.target;
    setDraft(d => ({ ...d, [name]: value }));
  };

  const handleSort = (field) => {
    const dir = field === applied.sortField && applied.sortDir === 'asc' ? 'desc' : 'asc';
    setApplied(a => ({ ...a, sortField: field, sortDir: dir }));
    setPage(0);
  };

  const openEdit = (company) => {
    setEditCompany(company);
    setEditForm(Object.fromEntries(
      Object.keys(EMPTY_EDIT).map(k => [k, company[k] || ''])
    ));
    setEditError('');
  };
  const closeEdit = () => { setEditCompany(null); setEditForm(EMPTY_EDIT); setEditError(''); };

  const openView = (company) => {
    setViewCompany(company);
    setViewTab('details');
  };
  const closeView = () => { setViewCompany(null); };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm(f => ({ ...f, [name]: value }));
  };

  const handleEditSave = async () => {
    setEditLoading(true);
    setEditError('');
    try {
      const payload = {};
      Object.keys(editForm).forEach(k => {
        if (editForm[k] !== (editCompany[k] || '')) payload[k] = editForm[k] || null;
      });
      if (Object.keys(payload).length === 0) { closeEdit(); return; }
      await updateCompany(editCompany.domain, payload);
      closeEdit();
      fetchCompanies();
    } catch (e) {
      setEditError(e.message || 'Failed to update company.');
    } finally {
      setEditLoading(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (field !== applied.sortField) return (
      <svg className="co-sort-icon neutral" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="7 15 12 20 17 15" opacity="0.4" />
        <polyline points="7 9 12 4 17 9" opacity="0.4" />
      </svg>
    );
    return applied.sortDir === 'asc' ? (
      <svg className="co-sort-icon active" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="7 9 12 4 17 9" />
      </svg>
    ) : (
      <svg className="co-sort-icon active" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="7 15 12 20 17 15" />
      </svg>
    );
  };

  return (
    <div className="co-root">

      <div className="co-toolbar">
        <div className="co-search-wrap">
          <svg className="co-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="co-search"
            placeholder="Search domain, company, industry, location…"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
          />
          {search && (
            <button className="co-search-clear" onClick={() => { setSearch(''); setPage(0); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        <button className={`co-filter-btn ${activeFilterCount > 0 ? 'co-filter-btn--active' : ''}`} onClick={openDrawer}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Filters
          {activeFilterCount > 0 && <span className="co-filter-badge">{activeFilterCount}</span>}
        </button>
      </div>

      {activeFilterCount > 0 && (
        <div className="co-chips">
          {applied.companyName && (
            <span className="co-chip">
              Company: {applied.companyName}
              <button onClick={() => { setApplied(a => ({ ...a, companyName: '' })); setPage(0); }}>✕</button>
            </span>
          )}
          {applied.industry && (
            <span className="co-chip">
              Industry: {applied.industry}
              <button onClick={() => { setApplied(a => ({ ...a, industry: '' })); setPage(0); }}>✕</button>
            </span>
          )}
          {applied.location && (
            <span className="co-chip">
              Location: {applied.location}
              <button onClick={() => { setApplied(a => ({ ...a, location: '' })); setPage(0); }}>✕</button>
            </span>
          )}
          {applied.employeeRange && (
            <span className="co-chip">
              Employees: {EMPLOYEE_RANGES.find(r => r.value === applied.employeeRange)?.label || applied.employeeRange}
              <button onClick={() => { setApplied(a => ({ ...a, employeeRange: '' })); setPage(0); }}>✕</button>
            </span>
          )}
          <button className="co-chip-clear" onClick={resetFilters}>Clear all</button>
        </div>
      )}

      {error && <div className="co-error">{error}</div>}

      {!loading && totalElements > 0 && (
        <p className="co-summary">
          Showing <strong>{companies.length}</strong> of <strong>{totalElements.toLocaleString()}</strong> companies
        </p>
      )}

      <div className="co-table-wrap">
        {loading ? (
          <div className="co-loading"><div className="co-spinner" /><span>Loading companies…</span></div>
        ) : companies.length === 0 ? (
          <div className="co-empty">
            <div className="co-empty-icon">🏢</div>
            <span>No companies found</span>
            <small>Try adjusting your search or filters</small>
          </div>
        ) : (
          <table className="co-table">
            <thead>
              <tr>
                <th className="co-th co-th-sm">#</th>
                <th className="co-th co-sortable" onClick={() => handleSort('companyName')}>
                  <div className="co-th-inner">Company <SortIcon field="companyName" /></div>
                </th>
                <th className="co-th co-sortable" onClick={() => handleSort('industry')}>
                  <div className="co-th-inner">Industry <SortIcon field="industry" /></div>
                </th>
                <th className="co-th co-sortable" onClick={() => handleSort('location')}>
                  <div className="co-th-inner">Location <SortIcon field="location" /></div>
                </th>
                <th className="co-th">Employees</th>
                <th className="co-th co-sortable" onClick={() => handleSort('contactCount')}>
                  <div className="co-th-inner">Contacts <SortIcon field="contactCount" /></div>
                </th>
                <th className="co-th co-th-action">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c, idx) => {
                const initials = c.domain ? c.domain.slice(0, 2).toUpperCase() : '??';
                return (
                  <tr key={c.domain} className="co-tr">
                    <td className="co-td co-td-muted"><span className="co-row-num">{page * pageSize + idx + 1}</span></td>
                    <td className="co-td">
                      <div className="co-company-cell">
                        <div className="co-avatar">{initials}</div>
                        <div>
                          <button
                            className="co-company-name"
                            onClick={() => onViewContacts && onViewContacts(c.domain)}
                            title="View contacts for this domain"
                          >
                            {c.domain}
                          </button>
                          <span className="co-sub">{c.companyName}</span>
                        </div>
                      </div>
                    </td>
                    <td className="co-td"><span className="co-badge">{c.industry}</span></td>
                    <td className="co-td co-td-muted">{c.location}</td>
                    <td className="co-td co-td-muted">{c.employeeCount}</td>
                    <td className="co-td">
                      <button
                        className="co-contacts-btn"
                        onClick={() => onViewContacts && onViewContacts(c.domain)}
                        title="View contacts"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        <span>{c.contactCount}</span>
                      </button>
                    </td>
                    <td className="co-td co-td-action">
                      <button className="co-view-btn" onClick={() => openView(c)} title="View">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      </button>
                      <button className="co-edit-btn" onClick={() => openEdit(c)} title="Edit company">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {totalElements > 0 && (
        <div className="co-pagination">
          <div className="co-page-left">
            <span className="co-page-info">Page {page + 1} of {totalPages}</span>
          </div>
          <div className="co-page-right">
            <button className="co-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i)
              .filter(i => i === 0 || i === totalPages - 1 || Math.abs(i - page) <= 2)
              .reduce((acc, i, idx, arr) => {
                if (idx > 0 && arr[idx - 1] !== i - 1) acc.push('…');
                acc.push(i);
                return acc;
              }, [])
              .map((item, idx) =>
                item === '…'
                  ? <span key={`e-${idx}`} className="co-page-ellipsis">…</span>
                  : <button key={item} className={`co-page-num ${item === page ? 'active' : ''}`} onClick={() => setPage(item)}>{item + 1}</button>
              )}
            <button className="co-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      )}

      {drawerOpen && (
        <div className="co-drawer-backdrop" onClick={closeDrawer}>
          <div className="co-drawer" onClick={e => e.stopPropagation()}>
            <div className="co-drawer-header">
              <h3 className="co-drawer-title">Filters</h3>
              <button className="co-modal-close" onClick={closeDrawer}>✕</button>
            </div>
            <div className="co-drawer-body">
              <div className="co-drawer-section">
                <label className="co-form-label">Company</label>
                <div className="co-input-clear-wrap">
                  <input className="co-form-input" name="companyName" placeholder="e.g. Acme Corp" value={draft.companyName} onChange={handleDraft} />
                  {draft.companyName && <button className="co-input-clear" type="button" onClick={() => setDraft(d => ({ ...d, companyName: '' }))}>✕</button>}
                </div>
              </div>
              <div className="co-drawer-section">
                <label className="co-form-label">Industry</label>
                <div className="co-input-clear-wrap">
                  <input className="co-form-input" name="industry" placeholder="e.g. Software" value={draft.industry} onChange={handleDraft} />
                  {draft.industry && <button className="co-input-clear" type="button" onClick={() => setDraft(d => ({ ...d, industry: '' }))}>✕</button>}
                </div>
              </div>
              <div className="co-drawer-section">
                <label className="co-form-label">Location</label>
                <div className="co-input-clear-wrap">
                  <input className="co-form-input" name="location" placeholder="e.g. New York" value={draft.location} onChange={handleDraft} />
                  {draft.location && <button className="co-input-clear" type="button" onClick={() => setDraft(d => ({ ...d, location: '' }))}>✕</button>}
                </div>
              </div>
              <div className="co-drawer-section">
                <label className="co-form-label">Employee Strength</label>
                <select className="co-form-input" name="employeeRange" value={draft.employeeRange} onChange={handleDraft}>
                  {EMPLOYEE_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>
            <div className="co-drawer-footer">
              <button className="co-btn co-btn-ghost" onClick={resetFilters}>Reset</button>
              <button className="co-btn co-btn-primary" onClick={applyFilters}>Apply Filters</button>
            </div>
          </div>
        </div>
      )}

      {editCompany && (
        <div className="co-modal-backdrop" onClick={closeEdit}>
          <div className="co-modal" onClick={e => e.stopPropagation()}>
            <div className="co-modal-header">
              <h2 className="co-modal-title">Edit Company</h2>
              <button className="co-modal-close" onClick={closeEdit}>✕</button>
            </div>
            {editError && <div className="co-modal-error">{editError}</div>}
            <div className="co-modal-body">
              <p className="co-modal-note">
                Changes will be applied to all <strong>{editCompany.contactCount}</strong> contact{editCompany.contactCount !== 1 ? 's' : ''} with email domain <strong>@{editCompany.domain}</strong>.
              </p>
              <div className="co-form-grid">
                {[
                  { name: 'companyName', label: 'Company Name' },
                  { name: 'companyUrl', label: 'Website URL' },
                  { name: 'industry', label: 'Industry' },
                  { name: 'companyLinkedinUrl', label: 'LinkedIn URL' },
                  { name: 'location', label: 'Location' },
                  { name: 'employeeCount', label: 'Employee Count' },
                ].map(({ name, label }) => (
                  <div key={name} className="co-form-field">
                    <label className="co-form-label">{label}</label>
                    <div className="co-input-clear-wrap">
                      <input className="co-form-input" type="text" name={name} value={editForm[name]} onChange={handleEditChange} />
                      {editForm[name] && <button className="co-input-clear" type="button" onClick={() => setEditForm(f => ({ ...f, [name]: '' }))}>✕</button>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="co-modal-footer">
              <button className="co-btn co-btn-ghost" onClick={closeEdit} disabled={editLoading}>Cancel</button>
              <button className="co-btn co-btn-primary" onClick={handleEditSave} disabled={editLoading}>
                {editLoading ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewCompany && (
        <div className="co-modal-backdrop" onClick={closeView}>
          <div className="co-view-modal" onClick={e => e.stopPropagation()}>
            <div className="co-view-header">
              <div className="co-view-title-row">
                <div className="co-view-avatar">
                  {(viewCompany.domain || '').slice(0, 2).toUpperCase() || '??'}
                </div>
                <div>
                  <h2 className="co-view-name">{viewCompany.companyName || viewCompany.domain}</h2>
                  <p className="co-view-sub">{viewCompany.domain}</p>
                </div>
              </div>
              <button className="co-modal-close" onClick={closeView}>✕</button>
            </div>

            <div className="co-view-tabs">
              {[
                { key: 'details', label: 'Details' },
                { key: 'audit', label: 'Audit Trail' },
              ].map(t => (
                <button
                  key={t.key}
                  className={`co-view-tab ${viewTab === t.key ? 'active' : ''}`}
                  onClick={() => setViewTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {viewTab === 'details' && (
              <div className="co-view-body">
                <div className="co-detail-grid">
                  <div className="co-detail-item">
                    <span className="co-detail-label">Company Name</span>
                    <span className="co-detail-value">{viewCompany.companyName || '—'}</span>
                  </div>
                  <div className="co-detail-item">
                    <span className="co-detail-label">Industry</span>
                    <span className="co-detail-value">{viewCompany.industry || '—'}</span>
                  </div>
                  <div className="co-detail-item">
                    <span className="co-detail-label">Location</span>
                    <span className="co-detail-value">{viewCompany.location || '—'}</span>
                  </div>
                  <div className="co-detail-item">
                    <span className="co-detail-label">Employee Count</span>
                    <span className="co-detail-value">{viewCompany.employeeCount || '—'}</span>
                  </div>
                  {viewCompany.companyUrl && (
                    <div className="co-detail-item">
                      <span className="co-detail-label">Website</span>
                      <a className="co-link" href={viewCompany.companyUrl} target="_blank" rel="noreferrer">{viewCompany.companyUrl}</a>
                    </div>
                  )}
                  {viewCompany.companyLinkedinUrl && (
                    <div className="co-detail-item">
                      <span className="co-detail-label">LinkedIn</span>
                      <a className="co-link" href={viewCompany.companyLinkedinUrl} target="_blank" rel="noreferrer">{viewCompany.companyLinkedinUrl}</a>
                    </div>
                  )}
                  <div className="co-detail-item">
                    <span className="co-detail-label">Contacts</span>
                    <span className="co-detail-value">{viewCompany.contactCount || 0}</span>
                  </div>
                </div>
              </div>
            )}

            {viewTab === 'audit' && (
              <div className="co-view-body">
                <div className="co-audit-list">
                  {[
                    { label: 'Created By', value: viewCompany.createdBy || '—' },
                    { label: 'Created On', value: viewCompany.createdAt ? new Date(viewCompany.createdAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—' },
                    { label: 'Last Modified By', value: viewCompany.updatedBy || '—' },
                    { label: 'Last Modified On', value: viewCompany.updatedAt ? new Date(viewCompany.updatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—' },
                  ].map(row => (
                    <div key={row.label} className="co-audit-row">
                      <span className="co-audit-label">{row.label}</span>
                      <span className="co-audit-value">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
